<script setup lang="ts">
import {getTranslation, getSettings, getShopStatus} from '@/ts/utilities.ts'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {computed, defineAsyncComponent, onMounted, ref, watch, onBeforeUnmount, onUnmounted} from "vue";
import {MapsData, MarkerData} from '@/interfaces/MapsViewInterface.ts'
import {debounce} from "vue-debounce";

const SlideOutMenu = defineAsyncComponent(() => import('@/components/SlideOutMenu.vue'))
const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const DealerItem = defineAsyncComponent(() => import('@/components/DealerItem.vue'))

const settings = getSettings()
const slideOutIsOpen = ref(false)
const locationPermission = ref('')
const dealersSearchResult = ref<MarkerData[]>([])
const query = ref('');
const doSearch = ref(true)
const endpoint = ref('/mobler-api/v2/shops/getshops')
let currentSearchToken = 0;


const openMenu = async () => {
	const newValue = !slideOutIsOpen.value
	let timeout = 0;

	if(newValue){
		timeout = 350
		const selectedDealers =  settings?.shopInfos;
		if(selectedDealers.length){
			doSearch.value = true;
			query.value = selectedDealers[0]?.City;
		}
	}

	setTimeout(() =>{
		slideOutIsOpen.value = newValue
	}, timeout)
}

const searchByGeoLocation = () => {
	doSearch.value = false;
	query.value = '';
	if ("geolocation" in navigator) {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				localStorage.setItem('moebler:persistant:location', JSON.stringify(position.coords))
				const token = ++currentSearchToken;
				getDealers().then((dealers) => {
						if (token === currentSearchToken) {
							dealersSearchResult.value = [];
							dealersSearchResult.value = findClosestDealers(position.coords.latitude, position.coords.longitude, dealers);
						}
				})

			},
			(error) => {
				console.error("Error getting location:", error);
			}
		);
	} else {
		console.error("Geolocation is not supported by this browser.");
	}
}

const getDistance = (lat1, lng1, lat2, lng2) => {
	const R = 6371; // Earth radius in km
	const dLat = (lat2 - lat1) * Math.PI / 180;
	const dLng = (lng2 - lng1) * Math.PI / 180;
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(lat1 * Math.PI / 180) *
		Math.cos(lat2 * Math.PI / 180) *
		Math.sin(dLng / 2) ** 2;
	return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const findClosestDealers = (userLat, userLng, dealers, maxResults = 3) => {
	if (!dealers?.shops?.length) return [];

	const sorted = dealers.shops
		.filter(d => d?.Lat && d?.Lng)
		.map(d => ({
			...d,
			distance: getDistance(userLat, userLng, d.Lat, d.Lng)
		}))
		.sort((a, b) => a.distance - b.distance);

	return sorted.slice(0, Math.min(maxResults, sorted.length));
}

const getDealers = async (query = '') => {
	dealersSearchResult.value = [];
	try {
		const resp = await fetch(`${endpoint.value}?query=${encodeURIComponent(query)}`);
		if (!resp.ok) throw new Error(`Failed to fetch dealers: ${resp.status}`);
		const data = await resp.json();
		return data;
	} catch (err) {
		console.error(err);
		return { shops: [] };
	}
};

const isLocalWebshopSelected = computed(() => {
	let shopname = settings?.shopname?.toLowerCase()

	if (shopname && shopname != "møblér" && shopname != "mobler" && shopname != "m%c3%b8bl%c3%a9r") {
		return true;
	}
	return false;
})

const handleGeolocationPermission = () => {
	searchByGeoLocation()
}

const search = () =>{
	const token = ++currentSearchToken;
	getDealers(query.value).then((result) => {
		if (token === currentSearchToken) {
			if(result.state){
				locationPermission.value = result.state;
			}

			dealersSearchResult.value = [...result.shops]
		}

	})
}

onMounted(() => {
	const savedLocation = JSON.parse(localStorage.getItem('moebler:persistant:shops') || 'null');
	const selectedDealers =  settings?.shopInfos;
	if (savedLocation && selectedDealers.length === 0) {
		handleGeolocationPermission();
	}

	if(selectedDealers.length > 0){
		doSearch.value = true;
		query.value = selectedDealers[0]?.City;
	}

	window.addEventListener('moebler:location:update', (e: CustomEvent) => {
		handleGeolocationPermission();
	});
	document.addEventListener('moebler:event:toggleShopMenu', openMenu)
});

onUnmounted(() =>{
	document.removeEventListener('moebler:event:toggleShopMenu', openMenu)
});


onBeforeUnmount(() => {
	currentSearchToken++;
});

watch(query, debounce((value: string) => {
	if(doSearch.value){
		search();
	}

}, 400))


</script>

<template>
	<div class="flex items-center justify-start gap-x-4 md:gap-x-6 lg:gap-x-4 xl:gap-x-6 w-full">

		<RenderAction
			v-if="!isLocalWebshopSelected"
			v-on:click="openMenu"
			action="button"
			theme="new-dark"
			size="sm"
		>
			<font-awesome-icon :icon="['fal', 'location-dot']"/>
			{{ getTranslation('HeaderNav.SelectShop', 'Vælg din butik') }}
			<font-awesome-icon :icon="['fas', 'chevron-down']" class="fa-sm"/>
		</RenderAction>

		<div class="flex justify-start items-center relative gap-3 hover:cursor-pointer" v-else v-on:click="openMenu">
			<font-awesome-icon :icon="['fal', 'location-dot']" />
			<span class="font-bold text-sm">{{ settings?.shopInfos[0]?.ShopName }}</span>
				<span class="text-sm text-black font-bold" v-if="settings?.shopInfos[0]?.CurrentOpeningInfoHasMore || !settings?.shopInfos[0]?.CurrentOpeningInfoShow">
					{{ getTranslation('Shop has Multiple', 'Se åbningstider') }}
				</span>
				<span class="text-sm flex gap-1" v-else>
					<span class="text-primary font-bold" v-if="settings?.shopInfos[0]?.CurrentOpeningInfoOpen">{{getTranslation('Shop is Open', 'Åben')}}</span>
					<span class="text-black font-bold" v-else>{{getTranslation('Shop is Closed', 'Lukket')}}</span>
					<span>·</span>
					<span>{{settings?.shopInfos[0]?.CurrentOpeningInfoText }}</span>
				</span>
			<font-awesome-icon :icon="['fas', 'chevron-down']" class="fa-sm"/>
		</div>

		<div class="ml-auto hidden lg:flex gap-5">
			<a
				v-if="isLocalWebshopSelected"
				:href="settings?.localShopPage"
				class="flex items-center flex-col md:flex-row md:gap-2 no-underline text-black hover:text-black/75"
			>
				<small class="text-xs mt-1 md:text-sm md:mt-0 max-md:text-center">
					{{ getTranslation('HeaderNav.LocalShop', 'Om os') }}
				</small>
			</a>
			<a
				v-if="settings?.inspirationPage"
				:href="settings?.inspirationPage"
				class="flex items-center flex-col md:flex-row md:gap-2 no-underline text-black hover:text-black/75"
			>
				<small class="text-xs mt-1 md:text-sm md:mt-0 max-md:text-center">
					{{ getTranslation('HeaderNav.Inspiration') }}
				</small>
			</a>
			<a
				v-if="settings?.offersPage"
				:href="settings?.offersPage"
				class="flex items-center flex-col md:flex-row md:gap-2 no-underline text-black hover:text-black/75"
			>
				<small class="text-xs mt-1 md:text-sm md:mt-0 max-md:text-center">
					{{ getTranslation('HeaderNav.Offers') }}
				</small>
			</a>
			<a
				v-if="settings?.avisLink"
				:href="settings?.avisLink"
				class="flex items-center flex-col md:flex-row md:gap-2 no-underline text-black hover:text-black/75"
			>
				<small class="text-xs mt-1 md:text-sm md:mt-0 max-md:text-center">
					{{ getTranslation('HeaderNav.News') }}
				</small>
			</a>
		</div>


	</div>
	<teleport to="body">
		<SlideOutMenu v-if="slideOutIsOpen" v-model="slideOutIsOpen" position="right" width="w-full md:w-[30rem]">
			<template #default>
				<div class="bg-white flex flex-col p-3 md:p-6">
					<!-- Title -->
					<h2 class="text-lg font-bold mb-6">
						{{ getTranslation('ShopSearch.Header', 'Vælg din Møblér-butik') }}
					</h2>

					<!-- Search -->
					<div class="mb-6">
						<label for="search" class="block text-sm font-medium text-gray-700 mb-2">
							{{ getTranslation('ShopSearch.Label', 'Søg efter butik') }}
						</label>
						<div class="relative">
							<input
								id="search"
								type="search"
								enterkeyhint="search"
								v-model="query"
								:placeholder="getTranslation('ShopSearch.Placeholder', 'Søg på postnummer eller by')"
								class="relative w-full text-left border bg-white border-grey text-black outline-none pl-4 pr-10 py-2 line-clamp-1 rounded-sm"
							/>

							<button v-if="locationPermission !== 'denied'" type="button"
									class="absolute inset-y-0 right-0 flex items-center p-3 text-gray-400 cursor-pointer z-50 active:text-gray-600"
									role="button"
									tabindex="0"
									v-on:touchstart="searchByGeoLocation"
									v-on:click="searchByGeoLocation">
								<font-awesome-icon :icon="['fal', 'location-crosshairs']"></font-awesome-icon>
							</button>
						</div>
					</div>

					<div v-if="!dealersSearchResult.length"
						 class="flex flex-col items-center justify-center border bg-white border-grey rounded-xl p-6 text-center rounded-sm min-h-[20rem]">
						<div class="w-[112px] h-[112px] flex items-center justify-center rounded-full bg-beige mb-4">
							<font-awesome-icon :icon="['fal', 'shop']" class="text-5xl"/>
						</div>
						<p class="text-sm text-gray-700 font-bold">
							{{
								getTranslation('ShopSearch.Text', 'Vælg din lokale butik, og se åbningstider og hvilke produkter du kan opleve i butikken.')
							}}

						</p>
					</div>
					<div class="flex flex-col gap-3" v-else>
						<DealerItem v-for="dealer in dealersSearchResult" :dealer="dealer"/>
					</div>
				</div>
			</template>
		</SlideOutMenu>
	</teleport>
</template>
